import * as _ from 'lodash';
import * as React from 'react';
import { useEffect, useState } from 'react';
import { styled } from '../../../../../theme';

export interface MeterWidgetProps {
  amount: number;
  tiers: {
    max: number;
  }[];
  unit: string;
  className?: any;
}

const formatter = new Intl.NumberFormat('en-US');

type Alignment = 'left' | 'center' | 'right';

export const MeterValue: React.FC<
  React.PropsWithChildren<{
    percent: number;
    align: Alignment;
    color: string;
  }>
> = ({ percent, align, children, color }) => {
  return (
    <S.MeterWrapper percent={percent}>
      <S.MeterWrapperInner>
        <S.Meter align={align} color={color}>
          {children}
        </S.Meter>
      </S.MeterWrapperInner>
    </S.MeterWrapper>
  );
};

export const MeterWidget: React.FC<MeterWidgetProps> = (props) => {
  // max percent
  const normalizeTiers = () => {
    let max = props.tiers[props.tiers.length - 1].max;

    if (max === -1 && props.tiers.length > 1) {
      let tiers = _.cloneDeep(props.tiers);
      tiers[props.tiers.length - 1].max = Math.max(tiers[props.tiers.length - 2].max, props.amount) * 1.25;
      return tiers;
    }

    if (max === -1 && props.tiers.length === 1) {
      return [
        {
          ...props.tiers[0],
          max: props.amount * 1.25
        }
      ];
    }

    return props.tiers;
  };

  const tiers = normalizeTiers();
  let infinity = props.tiers[props.tiers.length - 1].max == -1;

  let percent = Math.min((props.amount / _.max(tiers.map((t) => t.max))!) * 100, 100);
  let valuePosition: Alignment = 'left';
  if (percent > 20 && percent < 80) {
    valuePosition = 'center';
  } else if (percent >= 80) {
    valuePosition = 'right';
  }

  const [initial, setInitial] = useState(true);
  useEffect(() => {
    setInitial(false);
  }, []);

  return (
    <S.Container className={props.className}>
      <S.AbsoluteContainer height={23}>
        <MeterValue percent={percent} align={valuePosition} color="#ec6d6d">
          {formatter.format(props.amount)} {props.unit}
        </MeterValue>
      </S.AbsoluteContainer>
      <S.AbsoluteContainer height={16}>
        <S.Bar></S.Bar>
        <S.BarHighlighted percent={initial ? 0 : percent}></S.BarHighlighted>
      </S.AbsoluteContainer>
      <S.AbsoluteContainer height={23} style={{ marginTop: 3 }}>
        <MeterValue percent={0} align="left" color="gray">
          0 {props.unit}
        </MeterValue>

        {tiers.map((t, index) => {
          let value = `${formatter.format(t.max)} ${props.unit}`;

          if (index === tiers.length - 1 && infinity) {
            value = 'unlimited';
          }

          let position: 'left' | 'right' | 'center' = 'center';
          let percent = (t.max / tiers[tiers.length - 1].max) * 100;
          if (index === tiers.length - 1) {
            position = 'right';
            percent = 100;
          }

          return (
            <MeterValue key={t.max} percent={percent} align={position} color="gray">
              {value}
            </MeterValue>
          );
        })}
      </S.AbsoluteContainer>
    </S.Container>
  );
};
namespace S {
  const FONT_SIZE = 15;

  export const AbsoluteContainer = styled.div<{ height: number }>`
    position: relative;
    height: ${(p) => p.height}px;
  `;

  export const Bar = styled.div`
    border-radius: 20px;
    height: 100%;
    left: 0;
    top: 0;
    background: ${(p) => p.theme.dialog.background};
  `;

  export const MeterWrapper = styled.div<{ percent: number }>`
    left: ${(p) => p.percent}%;
    position: absolute;
    top: 0;
  `;

  export const MeterWrapperInner = styled.div`
    position: relative;
  `;

  const positioning = { left: 'left: 0', right: 'right: 0', center: '' };

  export const Meter = styled.div<{ align: Alignment; color: string }>`
    font-size: ${FONT_SIZE}px;
    color: ${(p) => p.color};
    white-space: nowrap;
    position: absolute;
    ${(p) => positioning[p.align]};
  `;

  export const BarHighlighted = styled.div<{ percent: number }>`
    position: absolute;
    border-radius: 20px;
    height: 100%;
    left: 0;
    top: 0;
    background: linear-gradient(90deg, #ba00ff, #ec6d6d);
    width: ${(p) => p.percent}%;
    transition: width 1s;
  `;

  export const Container = styled.div`
    position: relative;
  `;
}
